<template>
  <div>
    <div id="registrationsReport" class="wrrapper">
      <page-heading title="Patient Registrations Report" border></page-heading>
      <page-heading class="no-print">
        <template #content>
          <div class="flex align-center">
            <Select
              placeholder="Filter by"
              :loading="loading"
              :options="listDoctors"
              :input="handleFilter"
            />
            <DateRangePicker @update="updateDate" />
            <Input
              class="mr-1"
              type="text"
              placeholder="Search..."
              @enter="handleSearch"
              id="search"
              v-model="searchQuery"
              icon="mdi-magnify"
              :handleSearch="handleSearch"
              :handleReset="handleReset"
              :reset="searchQuery"
            />
            <div class="flex align-center cursor" @click="printReport">
              <img src="@/assets/print-icon.png" alt="print" />
            </div>
            <ExportButtons
              style="margin-left: 10px;"
              @pdf="handleExportPdf"
              @excel="handleExportExcel"
            />
            <span
              style="margin-top: 0.2rem; margin-left: 10px;"
              title="Printer Settings"
              ><i
                style="font-size: 1.2rem; cursor: pointer;"
                class="mdi mdi-cog"
                @click="$modal.show('printerSettings')"
              ></i
            ></span>
          </div>
        </template>
      </page-heading>
      <div class="registrations-report mb-base">
        <data-table :loading="loading" :cols="columns" :rows="report">
          <template slot="action" slot-scope="{ actions }">
            <dropdown-menu
              :rowData="actions"
              :handleItemClick="handlePrint"
              :items="['Print Receipt', 'Print Full Invoice']"
            />
          </template>
        </data-table>
      </div>
      <div class="mb-base flex justify-end mr-3 pb-2">
        <div>
          <div class="stats">
            <div class="stats__title">Gross Total:</div>
            <div class="stats__value">{{ grossTotal.toLocaleString() }}</div>
          </div>
          <div class="stats">
            <div class="stats__title">Total Study Cancel:</div>
            <div class="stats__value">
              {{ totalStudyCanceled.toLocaleString() }}
            </div>
          </div>
          <div class="stats">
            <div class="stats__title">Reservation Cancel:</div>
            <div class="stats__value">
              {{ reservationCanceled.toLocaleString() }}
            </div>
          </div>
          <div class="stats">
            <div class="stats__title">Total Discount:</div>
            <div class="stats__value">
              {{ discount.toLocaleString() }}
            </div>
          </div>
          <div class="stats">
            <div class="stats__title">Net Total:</div>
            <div class="stats__value">
              {{ netTotal }}
            </div>
          </div>
        </div>
      </div>
    </div>
    <div id="laterInvoice">
      <PrintTable
        :refferedTo="invoice.refferedTo"
        :registerPatient="invoice"
        invoiceType="laterInvoice"
        :rows="rows"
        :total="subtotal"
        :discount="discountAmount"
        :patientGender="getPatientGender"
        :invoiceNo="invoiceNo"
        :isOther="isOther"
      />
    </div>
    <div id="rePrintInvoice">
      <PrintTable
        :refferedTo="invoice.refferedTo"
        :registerPatient="invoice"
        invoiceType="rePrintInvoice"
        :rows="rows"
        :total="subtotal"
        :discount="discountAmount"
        :patientGender="getPatientGender"
        :invoiceNo="invoiceNo"
        :isOther="isOther"
      />
      <PrintTable
        :refferedTo="invoice.refferedTo"
        :registerPatient="invoice"
        invoiceType="rePrintInvoice"
        :rows="rows"
        :total="subtotal"
        :discount="discountAmount"
        :patientGender="getPatientGender"
        :invoiceNo="invoiceNo"
        :isOther="isOther"
      />
    </div>
    <div id="externalInvoice">
      <ExternalInvoice
        :invoiceNo="invoiceNo"
        :patient="a4PrintPatient || invoice"
        :searchResult="a4PrintPatient || invoice"
        :refferedTo="invoice.refferedTo || {}"
        :rows="rows"
        :charges="subtotal"
        :isOther="isOther"
        :discount="discountAmount"
        :branch_logo="branchLogoUrl"
        :org_logo="bussinessLogoUrl"
        :selectedDiscountType="selectedDiscountPanel"
        :referredBy="invoice.refferedBy || ''"
        :invoiceType="'rePrintInvoice'"
        :showReceiptCopies="!a4PrescriptionOnly"
        :patientGender="getPatientGender"
      />
    </div>
    <AppModal
      name="printConfirmation"
      title="Print Selection"
      height="auto"
      width="420"
    >
      <template v-slot:content>
        <div class="print-modal-content">
          <!-- Printer Connection Status -->
          <div
            :class="[
              'printer-status-bar',
              printerIsConnected ? 'connected' : 'disconnected'
            ]"
          >
            <span
              :class="[
                'mdi mdi-20px',
                printerIsConnected ? 'mdi-printer-check' : 'mdi-printer-off'
              ]"
            ></span>
            <div class="printer-info">
              <span class="printer-label">{{
                printerIsConnected ? "Ready" : "Not Connected"
              }}</span>
              <span class="printer-name">{{
                printerIsConnected ? printerName : "Connect thermal printer"
              }}</span>
            </div>
            <Button
              v-if="!printerIsConnected"
              title="Connect"
              className="button--primary button--xs"
              :onClick="connectPrinter"
            />
          </div>

          <div class="print-options">
            <p class="options-title">Select copies to print:</p>

            <!-- Patient Copy -->
            <div
              class="print-option"
              :class="{ selected: printPatientCopy }"
              @click="printPatientCopy = !printPatientCopy"
            >
              <div class="option-icon patient">
                <span class="mdi mdi-account"></span>
              </div>
              <div class="option-text">
                <span class="option-label">Customer Copy</span>
                <span class="option-desc">Receipt for patient</span>
              </div>
              <div class="option-check">
                <span
                  v-if="printPatientCopy"
                  class="mdi mdi-check-circle"
                ></span>
                <span
                  v-else
                  class="mdi mdi-checkbox-blank-circle-outline"
                ></span>
              </div>
            </div>

            <!-- Official Copy -->
            <div
              class="print-option"
              :class="{ selected: printOfficialCopy }"
              @click="printOfficialCopy = !printOfficialCopy"
            >
              <div class="option-icon official">
                <span class="mdi mdi-file-document"></span>
              </div>
              <div class="option-text">
                <span class="option-label">{{
                  isOther === "Laboratory" ? "Lab Copy" : "Doctor Copy"
                }}</span>
                <span class="option-desc">Official records</span>
              </div>
              <div class="option-check">
                <span
                  v-if="printOfficialCopy"
                  class="mdi mdi-check-circle"
                ></span>
                <span
                  v-else
                  class="mdi mdi-checkbox-blank-circle-outline"
                ></span>
              </div>
            </div>
          </div>
        </div>
      </template>
      <template v-slot:footer>
        <div class="print-modal-footer">
          <a href="#" @click.prevent="switchToA4" class="a4-link">
            <span class="mdi mdi-file-outline"></span> Use A4 Print
          </a>
          <div class="footer-buttons">
            <button
              class="btn-cancel"
              @click="$modal.hide('printConfirmation')"
            >
              Cancel
            </button>
            <button
              class="btn-print"
              :disabled="
                !printerIsConnected || (!printPatientCopy && !printOfficialCopy)
              "
              @click="executePrint"
            >
              <span class="mdi mdi-printer"></span> Print
            </button>
          </div>
        </div>
      </template>
    </AppModal>

    <AppModal name="a4AfterThermal" title="A4 Print" height="auto" width="420">
      <template v-slot:content>
        <div class="print-modal-content">
          <p class="options-title">
            Thermal print done. Do you want to print the Doctor prescription on
            A4 as well?
          </p>
        </div>
      </template>
      <template v-slot:footer>
        <div class="print-modal-footer">
          <div
            class="footer-buttons"
            style="justify-content: flex-end; width: 100%;"
          >
            <button class="btn-cancel" @click="skipA4AfterThermal">
              Skip
            </button>
            <button class="btn-print" @click="printA4AfterThermal">
              <span class="mdi mdi-file-outline"></span> Print A4
            </button>
          </div>
        </div>
      </template>
    </AppModal>

    <AppModal
      name="printerSettings"
      title="Printer Settings"
      height="auto"
      width="400"
    >
      <template v-slot:content>
        <div class="printer-settings-modal-body">
          <p class="section-title">Select Default Printer</p>
          <div class="printer-grid">
            <!-- A4 Card -->
            <div
              class="sel-card"
              :class="{ active: printerType === 'A4' }"
              @click="
                printerType = 'A4';
                savePrinterType();
              "
            >
              <div class="sel-card-icon">
                <span class="mdi mdi-file-document-outline"></span>
              </div>
              <div class="sel-card-content">
                <h6>A4 Printer</h6>
                <p>Standard documents</p>
              </div>
              <div class="sel-card-radio">
                <div class="radio-circle"></div>
              </div>
            </div>

            <!-- Thermal Card -->
            <div
              class="sel-card"
              :class="{ active: printerType === 'Thermal' }"
              @click="
                printerType = 'Thermal';
                savePrinterType();
              "
            >
              <div class="sel-card-icon">
                <span class="mdi mdi-printer-pos"></span>
              </div>
              <div class="sel-card-content">
                <h6>Thermal Printer</h6>
                <p>Receipt printing</p>
              </div>
              <div class="sel-card-radio">
                <div class="radio-circle"></div>
              </div>
            </div>
          </div>

          <!-- Thermal Status Block -->
          <transition name="fade">
            <div v-if="printerType === 'Thermal'" class="printer-status-block">
              <div
                class="status-badge"
                :class="
                  printerIsConnected ? 'bg-success-light' : 'bg-warning-light'
                "
              >
                <span
                  :class="[
                    'mdi mr-1',
                    printerIsConnected ? 'mdi-check-circle' : 'mdi-alert-circle'
                  ]"
                ></span>
                {{ printerIsConnected ? "Connected" : "Action Required" }}
              </div>

              <div class="status-detail mt-3">
                <div
                  class="status-icon-box"
                  :class="{ inactive: !printerIsConnected }"
                >
                  <span
                    :class="[
                      'mdi',
                      printerIsConnected ? 'mdi-usb-port' : 'mdi-usb-port-off'
                    ]"
                  ></span>
                </div>
                <div class="status-text-box">
                  <span class="device-name">{{
                    printerIsConnected ? printerName : "Printer Not Found"
                  }}</span>
                  <span class="device-hint">{{
                    printerIsConnected
                      ? "Ready for printing"
                      : "Connect via USB"
                  }}</span>
                </div>
                <Button
                  :title="printerIsConnected ? 'Configure' : 'Connect'"
                  :className="
                    printerIsConnected
                      ? 'button--transparent no-vertical-padding'
                      : 'button--primary no-vertical-padding'
                  "
                  style="min-width: 90px; height: 32px;"
                  :onClick="connectPrinter"
                />
              </div>
            </div>
          </transition>
        </div>
      </template>
      <template v-slot:footer>
        <div class="modal-footer-actions">
          <Button
            title="Close"
            className="button--primary"
            :onClick="() => $modal.hide('printerSettings')"
          />
        </div>
      </template>
    </AppModal>
  </div>
</template>

<script>
import DataTable from "@/components/DataTable.vue";
import DateRangePicker from "@/components/DateRangePIcker.vue";
import DropdownMenu from "@/components/DropdownMenu.vue";
import PrintTable from "@/components/PrintTable.vue";
import Input from "@/components/Input.vue";
import printDiv from "@/utils/print";
import Select from "@/components/Select.vue";
import { formatedDate, sumRows, formatNumbers } from "@/utils/helpers";
import { formatGender, getBranchLogo } from "../../../utils/helpers";
import { mapActions, mapGetters } from "vuex";
import ExternalInvoice from "@/components/externalInvoice";
import ThermalPrintService from "@/utils/ThermalPrintService";
import ExportButtons from "@/components/ExportButtons.vue";
import { exportReportPdf, exportReportExcel } from "@/utils/reportExport";
import moment from "moment";

export default {
  components: {
    DateRangePicker,
    DropdownMenu,
    PrintTable,
    DataTable,
    Select,
    Input,
    ExternalInvoice,
    ExportButtons,
    AppModal: () => import("@/components/Modal.vue"),
    Button: () => import("@/components/Button.vue")
  },
  data() {
    return {
      filters: {
        end_date: formatedDate(),
        start_date: formatedDate(),
        service_head: "",
        doctor_id: ""
      },
      searchQuery: null,
      bussinessLogoUrl: "",
      branchLogoUrl: "",
      printerIsConnected: false,
      printerName: "",
      printDetailsData: null,
      printPatientCopy: true,
      printOfficialCopy: false,
      pendingA4AfterThermal: false,
      a4PrescriptionOnly: false,
      a4PrintPatient: null,
      printerType: localStorage.getItem("printer_type") || "Thermal",

      columns: [
        { column: "serial", label: "#", width: "30" },
        // {
        //   column: "date",
        //   label: "Date",
        //   sortable: true,
        // },
        {
          column: "patientname",
          label: "Patient",
          sortable: true
        },
        {
          column: "responsible",
          label: "F/Name",
          sortable: true
        },
        {
          column: "service",
          label: "Service",
          sortable: true
        },
        {
          column: "created_name",
          label: "Created By",
          sortable: true
        },
        {
          column: "discount",
          label: "Discount",
          sortable: true
        },
        {
          column: "status",
          label: "Status",
          sortable: true
        },
        {
          column: "invoice",
          label: "Invoice No",
          sortable: true
        },
        {
          column: "mr_no",
          label: "mr no",
          sortable: true
        },
        {
          column: "rate",
          label: "Price",
          sortable: true
        },
        {
          column: "amount",
          label: "Amount",
          sortable: true
        },
        {
          column: "quantity",
          label: "Quantity"
        },
        {
          column: "total",
          label: "Amount"
        },
        {
          column: "action",
          label: "actions"
        }
      ]
    };
  },
  methods: {
    ...mapActions("reports", ["patientDailyReport"]),
    ...mapActions("doctors", ["doctorList"]),
    ...mapActions("expenses", ["fetchBranch", "currentSelectBranch"]),
    getUnitPrice(item) {
      return Number(item?.rate ?? item?.amount ?? 0);
    },
    getLineAmount(item) {
      const quantity = Number(item?.quantity ?? 0);
      return this.getUnitPrice(item) * quantity;
    },
    onThermalPrinterMockChanged(event) {
      const { connected, name } = event.detail || {};
      this.printerIsConnected = Boolean(connected);
      this.printerName = name || "";
    },
    savePrinterType() {
      localStorage.setItem("printer_type", this.printerType);
      this.$toasted
        .success(`Printer type set to ${this.printerType}`)
        .goAway(3000);
      if (this.printerType === "Thermal") {
        this.checkPrinterConnection();
      }
    },
    async checkPrinterConnection() {
      const thermalService = ThermalPrintService.getInstance();
      if (thermalService.isMockMode()) {
        await thermalService.autoConnect();
        this.printerIsConnected = thermalService.isConnected;
        this.printerName = thermalService.deviceName;
        return;
      }
      if (thermalService.isSupported()) {
        try {
          const devices = await navigator.usb.getDevices();
          if (devices.length > 0) {
            this.printerIsConnected = true;
            this.printerName = devices[0].productName || "USB Printer";
          } else {
            await thermalService.autoConnect();
            this.printerIsConnected = thermalService.isConnected;
            this.printerName = thermalService.deviceName;
          }
        } catch (err) {
          console.warn("[ThermalPrint] Silent connection check failed:", err);
        }
      }
    },
    async handlePrint(val, rowData) {
      const payload = {
        invoice_no: rowData.invoice,
        patiantId: rowData.patient_id
      };

      try {
        await this.$store.dispatch("receptionist/printInvoice", payload);
      } catch (error) {
        console.error(error);
        this.$toasted.show("Invoice print failed").goAway("5000");
        return;
      }

      // Use persistent printerType
      if (this.printerType !== "Thermal") {
        const isDoctor = rowData?.is_general_appointment;
        await this.printA4(val, rowData, {
          prescriptionOnly: isDoctor && val !== "Print Receipt"
        });
        return;
      }

      // If Thermal, prepare data and show modal
      const histories = this.invoice.histories || [];
      const grossTotal = histories.reduce(
        (sum, item) => sum + this.getLineAmount(item),
        0
      );
      const totalDiscount = histories.reduce(
        (sum, item) => sum + (item.discount || 0),
        0
      );
      const netTotal = grossTotal - totalDiscount;

      const isRadiology = this.isOther === "Radiology";
      const isTenant1 = this.user?.tenantId === 1;

      this.printDetailsData = {
        val,
        apiResponse: this.invoice,
        rowData: rowData,
        isDoctorAppointment: Boolean(rowData?.is_general_appointment),
        printOptions: {
          dualLogo: isTenant1 && isRadiology
        },
        thermalData: {
          organizationName: this.user?.bussiness?.name || "Family Hospital",
          mrNo: this.invoice.mrNo || rowData.mr_no || this.invoice.mr_no,
          token:
            this.rows?.[0]?.token || this.invoice?.histories?.[0]?.token || "-",
          serviceHeader:
            this.rows?.[0]?.service ||
            (this.isOther === "Radiology"
              ? "ULTRASOUND"
              : (this.isOther || "").toUpperCase()),
          patientName:
            this.invoice.name ||
            this.invoice.patientname ||
            rowData.patientname,
          fatherHusbandName:
            this.invoice.fatherOrHusbandName ||
            this.invoice.father_husband_name ||
            rowData.fatherOrHusbandName ||
            rowData.father_husband_name ||
            "-",
          age: this.invoice.age,
          gender: this.getPatientGender,
          invoiceNo: rowData.invoice || this.invoice.invoiceNo,
          date: moment(
            this.invoice?.histories?.[0]?.created_at ||
              rowData.created_at ||
              new Date()
          ).format("DD/MM/YYYY"),
          referredBy: this.invoice.refferedBy || rowData.referred_by || "-",
          panelName: this.invoice.panel_name || rowData.panel_name || null,
          invoiceStatus:
            this.invoice?.histories?.[0]?.invoice_status ||
            rowData.invoice_status,
          rows: histories.map(item => ({
            service: (
              item.service ||
              item.name ||
              item.charge?.service?.name ||
              ""
            ).toUpperCase(),
            price: this.getUnitPrice(item),
            subtotal: this.getLineAmount(item)
          })),
          total: grossTotal,
          discount: totalDiscount,
          netTotal: netTotal,
          printedBy: this.user?.full_name || this.user?.name || "Staff",
          printDate: moment(
            this.invoice?.histories?.[0]?.created_at ||
              rowData.created_at ||
              new Date()
          ).format("dddd, MMMM Do, YYYY, h:mm:ss a"),
          branch: this.user?.branch?.name || "Main Branch"
        }
      };

      if (this.printerType === "Thermal") {
        const thermalService = ThermalPrintService.getInstance();
        if (!thermalService.isConnected) {
          await thermalService.autoConnect();
        }
        this.printerIsConnected = thermalService.isConnected;
        this.printerName = thermalService.deviceName;

        this.$modal.show("printConfirmation");
      }
    },

    buildPrintPatient(rowData = {}) {
      const inv = this.invoice || {};
      return {
        name: inv.name || inv.patientname || rowData.patientname,
        fatherOrHusbandName:
          inv.fatherOrHusbandName ||
          inv.father_husband_name ||
          rowData.fatherOrHusbandName ||
          rowData.father_husband_name,
        age: inv.age || rowData.age,
        mr_no: inv.mr_no || inv.mrNo || rowData.mr_no,
        address: inv.address || rowData.address,
        gender: inv.gender || rowData.gender
      };
    },
    preloadPrintLogos() {
      const sources = [this.bussinessLogoUrl, this.branchLogoUrl].filter(
        src => typeof src === "string" && src
      );
      return Promise.all(
        sources.map(
          src =>
            new Promise(resolve => {
              const img = new Image();
              img.onload = img.onerror = () => resolve();
              img.src = src;
            })
        )
      );
    },
    async printA4(val, rowData, options = {}) {
      const isDoctor = Boolean(rowData?.is_general_appointment);
      const prescriptionOnly =
        options.prescriptionOnly !== undefined
          ? options.prescriptionOnly
          : isDoctor && val !== "Print Receipt";

      this.a4PrescriptionOnly = Boolean(prescriptionOnly && isDoctor);
      this.a4PrintPatient = this.buildPrintPatient(rowData);

      await this.fetchBranch();
      const branchId =
        this.invoice?.histories?.[0]?.branchId || this.user?.userBranchId;
      if (branchId && this.fetchUserBranch) {
        const logo = getBranchLogo(this.fetchUserBranch, branchId);
        if (typeof logo === "string" && logo) {
          this.branchLogoUrl = logo;
          this.currentSelectBranch(logo);
        }
      }
      this.bussinessLogoUrl = this.user?.bussinessLogoUrl || "";

      await this.preloadPrintLogos();
      await this.$nextTick();
      await this.$nextTick();

      const details = {
        mrNo:
          this.a4PrintPatient?.mr_no ||
          this.invoice?.mr_no ||
          this.invoice?.mrNo ||
          rowData?.mr_no,
        invoiceNo: this.invoiceNo
      };

      if (val === "Print Receipt") {
        this.a4PrescriptionOnly = false;
        printDiv("laterInvoice", details);
        return;
      }

      if (isDoctor) {
        printDiv("externalInvoice", details);
        if (this.a4PrescriptionOnly) {
          setTimeout(() => {
            this.a4PrescriptionOnly = false;
          }, 2000);
        }
        return;
      }

      this.a4PrescriptionOnly = false;
      printDiv("rePrintInvoice", details);
    },

    async connectPrinter() {
      try {
        const thermalService = ThermalPrintService.getInstance();
        const name = await thermalService.requestDevice();
        this.printerIsConnected = true;
        this.printerName = name;
        const label = thermalService.isMockMode()
          ? `Mock printer ready: ${name}`
          : `Printer connected: ${name}`;
        this.$toasted.success(label).goAway(3000);
      } catch (error) {
        if (process.env.NODE_ENV === "development") {
          ThermalPrintService.enableMockMode();
          this.printerIsConnected = true;
          this.printerName = "Mock Thermal Printer (Dev)";
          this.$toasted
            .success("Using mock thermal printer (dev mode)")
            .goAway(3000);
          return;
        }
        console.error("Connection failed:", error);
        this.$toasted.error("Failed to connect to printer").goAway(3000);
      }
    },

    async executePrint() {
      if (!this.printDetailsData) return;
      const {
        thermalData,
        printOptions: initialOptions
      } = this.printDetailsData;

      try {
        const thermalService = ThermalPrintService.getInstance();
        const printOptions = {
          printPatient: this.printPatientCopy,
          printOfficial: this.printOfficialCopy,
          dualLogo: initialOptions?.dualLogo || false
        };

        await thermalService.printCustom(
          thermalData,
          this.isOther,
          printOptions
        );
        this.$modal.hide("printConfirmation");
        this.printerIsConnected = thermalService.isConnected;
        this.$toasted.success("Printed successfully").goAway(3000);

        const { isDoctorAppointment } = this.printDetailsData || {};
        if (isDoctorAppointment && this.printPatientCopy) {
          await this.printA4AfterThermal();
          return;
        }
        this.printDetailsData = null;
      } catch (error) {
        console.error("Thermal printing failed", error);
        this.$toasted
          .error("Thermal print failed, falling back to A4...")
          .goAway(3000);
        this.$modal.hide("printConfirmation");
        await this.switchToA4();
      }
    },

    skipA4AfterThermal() {
      this.pendingA4AfterThermal = false;
      this.$modal.hide("a4AfterThermal");
      this.printDetailsData = null;
    },

    async printA4AfterThermal() {
      this.pendingA4AfterThermal = false;
      this.$modal.hide("a4AfterThermal");
      const { rowData } = this.printDetailsData || {};
      await this.printA4("Print Full Invoice", rowData, {
        prescriptionOnly: true
      });
      this.printDetailsData = null;
    },

    async switchToA4() {
      if (!this.printDetailsData) return;
      const { val, rowData, isDoctorAppointment } = this.printDetailsData;
      this.$modal.hide("printConfirmation");
      await this.printA4(val || "Print Full Invoice", rowData, {
        prescriptionOnly: Boolean(isDoctorAppointment)
      });
      this.printDetailsData = null;
    },
    handleSearch() {
      this.filters.invoice_no = this.searchQuery.toLowerCase();
      this.patientDailyReport(this.filters);
    },
    handleReset() {
      this.searchQuery = null;
      this.filters.invoice_no = null;
      this.patientDailyReport(this.filters);
    },
    updateDate(val) {
      (this.filters.end_date = formatedDate(val.endDate)),
        (this.filters.start_date = formatedDate(val.startDate));
      this.patientDailyReport(this.filters);
    },
    printReport() {
      const details = {
        title: "Patient Reports",
        printedBy: this?.$store?.getters["auth/userInfo"].full_name,
        startDate: this.filters.start_date,
        endDate: this.filters.end_date,
        imageUrl: this.bussinessLogoUrl,
        imageUrl2: this.selectedBranch
      };
      printDiv("registrationsReport", details);
    },
    exportInput() {
      return {
        title: "Patient Registrations Report",
        dateRange: {
          start: this.filters.start_date,
          end: this.filters.end_date
        },
        printedBy: this.user && this.user.full_name,
        businessLogoUrl: this.bussinessLogoUrl,
        branchLogoUrl: this.branchLogoUrl,
        cols: this.columns,
        rows: this.report,
        summary: [
          { label: "Gross Total", value: this.grossTotal.toLocaleString() },
          {
            label: "Total Study Cancel",
            value: this.totalStudyCanceled.toLocaleString()
          },
          {
            label: "Reservation Cancel",
            value: this.reservationCanceled.toLocaleString()
          },
          { label: "Total Discount", value: this.discount.toLocaleString() },
          { label: "Net Total", value: this.netTotal }
        ],
        fileName: "patient-registrations-report"
      };
    },
    handleExportPdf() {
      exportReportPdf(this.exportInput());
    },
    handleExportExcel() {
      exportReportExcel(this.exportInput());
    },
    handleFilter(val) {
      const id = val && val.id !== null ? val.id : "";
      this.filters.service_head = id;
      this.patientDailyReport(this.filters);
    }
  },
  computed: {
    ...mapGetters({
      loading: "loading",
      patientReport: "reports/patientReport",
      listDoctors: "doctors/listDoctors",
      invoice: "receptionist/invoice",
      selectedBranch: "expenses/selectedBranch",
      user: "auth/userInfo",
      settings: "bussiness/settings",
      fetchUserBranch: "expenses/fetchUserBranch"
    }),
    invoiceNo() {
      return this?.invoice?.histories?.[0]?.invoice_no;
    },
    selectedDiscountPanel() {
      // Convert discountAmount to a string to avoid type error
      return this.invoice?.panel_name || "";
    },
    isOther() {
      const histories = this?.invoice?.histories || [];
      const status = histories.some(item => item.isOther == 0);
      return status ? "Radiology" : "Other";
    },
    report() {
      const status = [1, 3, 5];
      return this.patientReport.map(item => ({
        status: status.includes(item.history_status) ? 3 : item.history_status,
        total: this.getLineAmount(item),
        ...item
      }));
    },
    rows() {
      const histories = this?.invoice?.histories || [];
      return histories.map(item => ({
        discountValue: item.discount === 0 ? "" : item.discount,
        quantityNumber: item.quantity,
        price: this.getUnitPrice(item),
        subtotal: this.getLineAmount(item) - (item?.discount || 0),
        createdDate: item?.created_at,
        ...item.charge.service,
        token: item.token,
        service: item?.charge?.service?.lookup?.name || "TOKEN",
        invoice_status: item.invoice_status,
        isGenAppt: item.isGenAppt
      }));
    },
    subtotal() {
      const item = this?.invoice?.histories || [];
      const filterValues = item.map(item => this.getLineAmount(item));
      return sumRows(filterValues) - this.discountAmount;
    },
    discountAmount() {
      const item = this?.invoice?.histories || [];
      const filterValues = item.map(item => item.discount);
      return sumRows(filterValues);
    },
    discount() {
      const item =
        this.patientReport &&
        this.patientReport.filter(
          item => item.history_status !== 4 && item.history_status !== 7
        );
      const filterValues = item.map(item => item.discount);
      return sumRows(filterValues);
    },
    totalStudyCanceled() {
      const items = this.patientReport.filter(item => item.history_status == 7);
      const newItems = items.map(item => this.getLineAmount(item));
      return sumRows(newItems);
    },
    grossTotal() {
      const item = this.patientReport.map(item => this.getLineAmount(item));
      return sumRows(item);
    },
    reservationCanceled() {
      const items = this.patientReport.filter(
        item => item.history_status === 4
      );
      const newItems = items.map(item => this.getLineAmount(item));
      return sumRows(newItems);
    },
    netTotal() {
      const total =
        this.grossTotal -
        this.reservationCanceled -
        this.discount -
        this.totalStudyCanceled;
      return formatNumbers(total) || 0;
    },
    getBranchUrl() {
      return this.selectedBranch;
    },
    getPatientGender() {
      return formatGender(this.invoice.gender);
    }
  },
  mounted() {
    this.patientDailyReport(this.filters);
    this.doctorList("servicehead");
    this.fetchBranch();
    this.bussinessLogoUrl = this.user && this.user.bussinessLogoUrl;
    const branchId = this.user?.userBranchId;
    if (branchId && this.fetchUserBranch) {
      const logo = getBranchLogo(this.fetchUserBranch, branchId);
      if (typeof logo === "string" && logo) {
        this.branchLogoUrl = logo;
        this.currentSelectBranch(logo);
      }
    } else if (this.selectedBranch) {
      this.branchLogoUrl =
        typeof this.selectedBranch === "string" ? this.selectedBranch : "";
    }

    // Initial printer connection check
    console.log(
      "FinanceReport Mounted - Methods Available:",
      !!this.checkPrinterConnection
    );
    if (this.printerType === "Thermal") {
      this.checkPrinterConnection();
    }
    window.addEventListener(
      "thermal-printer-mock-changed",
      this.onThermalPrinterMockChanged
    );
  },
  beforeDestroy() {
    window.removeEventListener(
      "thermal-printer-mock-changed",
      this.onThermalPrinterMockChanged
    );
  }
};
</script>

<style>
@import "../../../assets/css/print.css";
@media print {
  .registrations-report .table table tr td span {
    font-size: 0.6875rem !important;
    line-height: 15px !important;
    letter-spacing: 1px !important;
    white-space: nowrap;
  }

  .registrations-report .table table thead tr th .label {
    font-size: 0.7rem !important;
    line-height: 18px !important;
    white-space: nowrap;
  }

  .stats .stats__title {
    width: 200px;
    font-size: 0.7rem !important;
    line-height: 18px !important;
  }

  .stats .stats__value {
    font-size: 0.7rem !important;
    line-height: 18px !important;
  }

  .date-range-wrrapper,
  .custom-select .vs__dropdown-toggle {
    border: unset;
  }

  .date-range-wrrapper .calendar {
    border: none;
    padding: 0;
    display: none !important;
  }

  .date-range-wrrapper .calender {
    display: none !important;
  }

  .vs__actions {
    display: none;
  }
}

.stats {
  display: flex;
  align-items: center;
}

.stats .stats__title {
  width: 200px;
  font-weight: 600;
  font-size: var(--fs-body);
  line-height: 30px;
  letter-spacing: 0.05em;
  text-transform: capitalize;
  color: var(--ink-900);
}

.stats .stats__value {
  font-weight: normal;
  font-size: var(--fs-body);
  line-height: 30px;
  letter-spacing: 0.05em;
  text-transform: capitalize;
  color: var(--ink-900);
}
/* Print Modal Styles */
.print-modal-content {
  padding: 0;
}

.printer-status-bar {
  display: flex;
  align-items: center;
  padding: 12px 16px;
  gap: 12px;
  border-bottom: 1px solid var(--line);
}

.printer-status-bar.connected {
  background: var(--danger-bg);
  /* reuse a muted tint; success tint not a named bg token — use surface-2 */
  background: var(--surface-2);
  color: var(--success);
}

.printer-status-bar.disconnected {
  background: var(--surface-2);
  color: var(--warning);
}

.printer-info {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.printer-label {
  font-weight: 600;
  font-size: var(--fs-body);
}

.printer-name {
  font-size: var(--fs-xs);
  opacity: 0.8;
}

.print-options {
  padding: 16px;
}

.options-title {
  font-size: var(--fs-sm);
  font-weight: 600;
  color: var(--muted);
  margin-bottom: 12px;
}

.print-option {
  display: flex;
  align-items: center;
  padding: 14px;
  border: 1px solid var(--line);
  border-radius: var(--radius-md);
  margin-bottom: 10px;
  cursor: pointer;
  transition: border-color var(--dur) var(--ease),
    background var(--dur) var(--ease);
  background: var(--surface-2);
}

.print-option:hover {
  border-color: var(--accent-500);
  background: var(--surface);
}

.print-option.selected {
  border-color: var(--accent-600);
  background: var(--accent-050);
}

.option-icon {
  width: 40px;
  height: 40px;
  border-radius: var(--radius-md);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  margin-right: 12px;
}

.option-icon.patient {
  background: var(--accent-050);
  color: var(--accent-600);
}

.option-icon.official {
  background: var(--surface-2);
  color: var(--muted);
}

.option-text {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.option-label {
  font-weight: 600;
  font-size: var(--fs-body);
  color: var(--text);
}

.option-desc {
  font-size: var(--fs-xs);
  color: var(--muted-soft);
}

.option-check {
  font-size: 22px;
}

.print-option.selected .option-check {
  color: var(--accent-600);
}

.print-option:not(.selected) .option-check {
  color: var(--line-strong);
}

.print-modal-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  border-top: 1px solid var(--line);
  background: var(--surface-2);
}

.a4-link {
  font-size: var(--fs-xs);
  color: var(--accent-600);
  text-decoration: none;
  display: flex;
  align-items: center;
  gap: 4px;
}

.a4-link:hover {
  text-decoration: underline;
  color: var(--accent-700);
}

.footer-buttons {
  display: flex;
  gap: 8px;
}

.btn-cancel {
  padding: 8px 16px;
  border: 1px solid var(--line);
  background: var(--surface);
  border-radius: var(--radius-sm);
  font-size: var(--fs-sm);
  color: var(--text);
  cursor: pointer;
  transition: background var(--dur) var(--ease);
}

.btn-cancel:hover {
  background: var(--surface-2);
}

.btn-print {
  padding: 8px 20px;
  border: none;
  background: var(--grad-accent);
  color: #fff;
  border-radius: var(--radius-sm);
  font-size: var(--fs-sm);
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: background var(--dur) var(--ease);
}

.btn-print:hover:not(:disabled) {
  background: var(--grad-accent-hover);
}

.btn-print:disabled {
  background: var(--line-strong);
  cursor: not-allowed;
}

.button--xs {
  padding: 4px 10px;
  font-size: var(--fs-xs);
}

.printer-settings-modal-body {
  padding: 1.5rem;
}

.section-title {
  font-weight: 700;
  font-size: var(--fs-h2);
  margin-bottom: 1.5rem;
  color: var(--ink-900);
}

.printer-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  margin-bottom: 2rem;
}

.sel-card {
  border: 1px solid var(--line);
  border-radius: var(--radius-lg);
  padding: 1rem;
  cursor: pointer;
  transition: border-color var(--dur) var(--ease),
    background var(--dur) var(--ease), box-shadow var(--dur) var(--ease);
  display: flex;
  flex-direction: column;
  position: relative;
  background: var(--surface);
}

.sel-card:hover {
  border-color: var(--accent-500);
  background: var(--accent-050);
}

.sel-card.active {
  border-color: var(--accent-600);
  background: var(--accent-050);
  box-shadow: var(--shadow-sm);
}

.sel-card-icon {
  width: 40px;
  height: 40px;
  background: var(--surface-2);
  border-radius: var(--radius-md);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1rem;
  transition: background var(--dur) var(--ease), color var(--dur) var(--ease);
}

.sel-card.active .sel-card-icon {
  background: var(--accent-600);
  color: #fff;
}

.sel-card-icon .mdi {
  font-size: 1.5rem;
}

.sel-card-content h6 {
  margin: 0 0 0.25rem 0;
  font-weight: 700;
  font-size: var(--fs-body);
  color: var(--ink-900);
}

.sel-card-content p {
  margin: 0;
  font-size: var(--fs-xs);
  color: var(--muted);
}

.sel-card-radio {
  position: absolute;
  top: 1rem;
  right: 1rem;
}

.radio-circle {
  width: 20px;
  height: 20px;
  border: 2px solid var(--line-strong);
  border-radius: 50%;
  position: relative;
}

.sel-card.active .radio-circle {
  border-color: var(--accent-600);
}

.sel-card.active .radio-circle::after {
  content: "";
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 10px;
  height: 10px;
  background: var(--accent-600);
  border-radius: 50%;
}

.printer-status-block {
  background: var(--surface-2);
  border-radius: var(--radius-lg);
  padding: 1.25rem;
  border: 1px dashed var(--line-strong);
}

.status-badge {
  display: inline-flex;
  align-items: center;
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: var(--fs-eyebrow);
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: var(--tracking-eyebrow);
}

.bg-success-light {
  background: var(--surface-2);
  color: var(--success);
}
.bg-warning-light {
  background: var(--surface-2);
  color: var(--warning);
}

.status-detail {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.status-icon-box {
  width: 48px;
  height: 48px;
  background: var(--surface);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-sm);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  color: var(--accent-600);
}

.status-icon-box.inactive {
  color: var(--muted-soft);
}

.status-text-box {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.device-name {
  font-weight: 700;
  font-size: var(--fs-sm);
  color: var(--ink-900);
}

.device-hint {
  font-size: var(--fs-xs);
  color: var(--muted);
}

.modal-footer-actions {
  display: flex;
  justify-content: flex-end;
  padding: 1rem 1.5rem;
  border-top: 1px solid var(--line);
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}
</style>
